import React, { useRef } from 'react';
import { DateInput, Button, Icon, TimeInput, Tooltip, DropDownMenu } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import './OpportunityTodosTimepicker.scss';
import T from 'Components/Helpers/translate';
import moment from 'moment-timezone';
import { useNow } from 'App/components/hooks';

type Props = {
	onChange: (e: { date: Date | null; time: string | null }) => void;
	onClear: () => void;
	date: Date;
	autofocus: boolean;
	time: string;
};

const OpportunityTodosTimepicker = ({ onChange, onClear, date, time, autofocus }: Props) => {
	const dropdownRef = useRef<HTMLDivElement>(null);
	const classes = new BemClass('OpportunityTodosTimepicker');
	const now = useNow();

	const timeChange = (e: { target: { value: Date } }) => {
		const newTime = e.target.value;

		const newDate = date ? moment(date).toDate() : moment().toDate();

		if (newTime) {
			newDate.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
		} else {
			newDate.setHours(0, 0, 0, 0);
		}

		onChange({ date: newTime || date ? newDate : null, time: newTime ? moment(newTime).format('HH:mm') : null });
	};

	const dateChange = (e: { target: { value: Date } }) => {
		const newDateInput = e.target.value;

		if (!newDateInput) {
			return onChange({ date: null, time: null });
		}

		const newDate = moment(newDateInput).toDate();

		if (date && time) {
			const oldDate = moment(date).toDate();
			newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
		}

		onChange({ date: newDate, time: time ? time : null });
	};

	const suggestedTime = moment(now)
		.add(30 - (moment(now).minute() % 30), 'minutes')
		.format('HH:mm');

	return (
		<div ref={dropdownRef} className={classes.b()}>
			<DropDownMenu
				align="right"
				useAnimation
				className={classes.elem('dropdown').b()}
				renderTrigger={(isExpanded, setExpanded) => (
					<Tooltip
						disabled={isExpanded}
						title={T('todo.opportunity.addDueDate')}
						position="top"
						distance={24}
					>
						<Button
							id="trigger"
							className={classes.elem('trigger').b()}
							onClick={setExpanded}
							size="sm"
							type="link"
							color="grey"
						>
							<Icon name="calendar" color={isExpanded || date || time ? 'medium-green' : undefined} />
						</Button>
					</Tooltip>
				)}
			>
				<div className={classes.elem('content').b()}>
					<div className={classes.elem('content-input').b()}>
						<DateInput
							closeOnSelect={true}
							value={date}
							placeholder={T('default.date')}
							onChange={dateChange}
							autofocus={autofocus}
						/>

						<TimeInput
							placeholder={T('default.time')}
							value={time ? date : undefined}
							defaultSelected={suggestedTime}
							asDate={true}
							onChange={timeChange}
						/>
					</div>

					{(date || time) && (
						<Button
							id="close-btn"
							type="link"
							size="sm"
							className={classes.elem('close-btn').b()}
							onClick={onClear}
						>
							<Icon name="calendar-times-o" />
						</Button>
					)}
				</div>
			</DropDownMenu>
		</div>
	);
};

export default OpportunityTodosTimepicker;
